import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {IntelligenceData} from '../models/intelligence-data.interface';
import {environment} from '../../../environments/environment';
import {Master} from '../../admin/models/master.interface';
import {IntelligenceSearchInterface} from '../models/intelligence-search.interface';

const INTELLIGENCE_API = environment.apiUrl + 'intelligence';
const INTELLIGENCE_REPORTING_API = environment.apiUrl + 'intelligencereporting';

@Injectable({
  providedIn: 'root'
})
export class IntelligenceService {

  constructor(private http: HttpClient) {
  }

  static buildSearchParams(search: IntelligenceSearchInterface): string {
    let searchParams = '';

    if (search.year) {
      searchParams += `?year=${search.year}`;
    }
    if (search.categories && search.categories.length > 0) {
      search.categories.forEach(category => searchParams += `&categories=${category}`);
    }
    if (search.eWasteCategories && search.eWasteCategories.length > 0) {
      if (searchParams.length > 0) {
        search.eWasteCategories.forEach(category => searchParams += `&eWasteCategories=${category}`);
      } else {
        search.eWasteCategories.forEach(category => searchParams += `?eWasteCategories=${category}`);
      }
    }
    if (search.responseType && search.responseType !== '') {
      if (searchParams.length > 0) {
        searchParams += `&responseType=${search.responseType}`;
      } else {
        searchParams += `?responseType=${search.responseType}`;
      }
    }
    if (search.order && search.order !== '') {
      searchParams += `&order=${search.order}`;
    }
    return searchParams;
  }

  getYears(): Observable<number[]> {
    return this.http.get<number[]>(`${INTELLIGENCE_API}/years`);
  }

  getCategories(): Observable<Master[]> {
    return this.http.get<Master[]>(`${INTELLIGENCE_REPORTING_API}/questioncategories/list`);
  }

  getEWasteCategories(): Observable<any> {
    return this.http.get<any>(`${INTELLIGENCE_API}/e-waste-legislation/categories`);
  }

  getEWasteLegislation(search: IntelligenceSearchInterface): Observable<IntelligenceData[]> {
    const searchParams = IntelligenceService.buildSearchParams(search);
    return this.http.get<IntelligenceData[]>(`${INTELLIGENCE_API}/e-waste-legislation` + searchParams);
  }

  getSystemsMap(region?: string): Observable<IntelligenceData[]> {
    let url = `${INTELLIGENCE_API}/systems-map`;
    if (region) {
      url += '?region=' + region;
    }
    return this.http.get<IntelligenceData[]>(url);
  }

  getVisibleFee(search: IntelligenceSearchInterface): Observable<IntelligenceData[]> {
    let url = `${INTELLIGENCE_API}/visible-fee`;
    if (search.responseType && search.responseType !== '') {
      url += `?responseType=${search.responseType}`;
    }
    return this.http.get<IntelligenceData[]>(url);
  }

  getReUseTargets(search: IntelligenceSearchInterface): Observable<IntelligenceData[]> {
    const searchParams = IntelligenceService.buildSearchParams(search);
    return this.http.get<IntelligenceData[]>(`${INTELLIGENCE_API}/re-use-targets` + searchParams);
  }

  getStandardMandatory(search: IntelligenceSearchInterface): Observable<IntelligenceData[]> {
    const searchParams = IntelligenceService.buildSearchParams(search);
    return this.http.get<IntelligenceData[]>(`${INTELLIGENCE_API}/weee-treatment-standard-mandatory` + searchParams);
  }

  getRetailVsMunicipalitiesVsOther(search: IntelligenceSearchInterface): Observable<[IntelligenceData[]]> {
    const searchParams = IntelligenceService.buildSearchParams(search);
    return this.http.get<[IntelligenceData[]]>(`${INTELLIGENCE_API}/retail-vs-municipalities-vs-other` + searchParams);
  }

  getWSRetailVsMunicipalitiesVsOther(search: IntelligenceSearchInterface): Observable<IntelligenceData[]> {
    const searchParams = IntelligenceService.buildSearchParams(search);
    return this.http.get<IntelligenceData[]>(`${INTELLIGENCE_API}/weeesystem/retail-vs-municipalities-vs-other` + searchParams);
  }

  getMembersAdhered(search: IntelligenceSearchInterface): Observable<IntelligenceData[]> {
    const searchParams = IntelligenceService.buildSearchParams(search);
    return this.http.get<IntelligenceData[]>(`${INTELLIGENCE_API}/members-adhered` + searchParams);
  }

  getNetworkRecyclingFacilities(search: IntelligenceSearchInterface): Observable<IntelligenceData[]> {
    const searchParams = IntelligenceService.buildSearchParams(search);
    return this.http.get<IntelligenceData[]>(`${INTELLIGENCE_API}/network-recycling-facilities` + searchParams);
  }

  getCollectionPointsOperated(search: IntelligenceSearchInterface): Observable<IntelligenceData[]> {
    const searchParams = IntelligenceService.buildSearchParams(search);
    return this.http.get<IntelligenceData[]>(`${INTELLIGENCE_API}/collection-points-operated` + searchParams);
  }

  getEmployeesPerPro(search: IntelligenceSearchInterface): Observable<IntelligenceData[]> {
    const searchParams = IntelligenceService.buildSearchParams(search);
    return this.http.get<IntelligenceData[]>(`${INTELLIGENCE_API}/employees-per-pro` + searchParams);
  }
}
